import React, { useState, useRef } from 'react';
import axios from 'axios';

interface TTSResult {
  audioUrl: string;
  latencyMs: number;
  audioSizeBytes: number;
  costEstimate: number;
}

/**
 * Azure Cognitive Services Neural TTS Prototype
 * Tests Microsoft Azure's Neural TTS API
 *
 * Features to test:
 * - Voice quality vs Web Speech API and OpenAI
 * - Available neural voices (50+ languages, 200+ voices)
 * - Speech Synthesis Markup Language (SSML) support
 * - Speed control via SSML
 * - Latency (API call + download time)
 * - Audio file size (for bandwidth estimates)
 * - Cost: $16/1M characters (500K free/month)
 *
 * Backend endpoint required: POST /api/app/tts-prototype/azure
 */
export const AzureTTSPrototype: React.FC = () => {
  const [text, setText] = useState(
    "Azure AI Speech provides industry-leading, lifelike neural text-to-speech voices. Neural text to speech supports more than 460 voices across more than 140 languages and locales. With neural text to speech, you can make interactions with chatbots and voice assistants more natural and engaging."
  );
  const [voice, setVoice] = useState<string>('en-US-JennyNeural');
  const [speed, setSpeed] = useState<number>(1.0);
  const [pitch, setPitch] = useState<string>('default');
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<TTSResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Popular Azure Neural voices (subset)
  const voices = [
    { id: 'en-US-JennyNeural', name: 'Jenny (US English, Female)', language: 'en-US' },
    { id: 'en-US-GuyNeural', name: 'Guy (US English, Male)', language: 'en-US' },
    { id: 'en-US-AriaNeural', name: 'Aria (US English, Female)', language: 'en-US' },
    { id: 'en-US-DavisNeural', name: 'Davis (US English, Male)', language: 'en-US' },
    { id: 'en-GB-SoniaNeural', name: 'Sonia (British English, Female)', language: 'en-GB' },
    { id: 'en-GB-RyanNeural', name: 'Ryan (British English, Male)', language: 'en-GB' },
    { id: 'en-AU-NatashaNeural', name: 'Natasha (Australian, Female)', language: 'en-AU' },
    { id: 'en-IN-NeerjaNeural', name: 'Neerja (Indian English, Female)', language: 'en-IN' }
  ];

  const pitchOptions = [
    { value: 'x-low', label: 'Very Low' },
    { value: 'low', label: 'Low' },
    { value: 'default', label: 'Default' },
    { value: 'high', label: 'High' },
    { value: 'x-high', label: 'Very High' }
  ];

  const handleGenerate = async () => {
    if (!text.trim()) {
      setError('Please enter text to convert');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setResult(null);

    const startTime = performance.now();

    try {
      // Call backend API (which will call Azure Neural TTS API)
      // Note: For testing, use proxy server: http://localhost:3003
      const apiBaseUrl = import.meta.env.VITE_TTS_PROXY_URL || 'http://localhost:3003';
      const response = await axios.post<TTSResult>(
        `${apiBaseUrl}/api/app/tts-prototype/azure`,
        {
          text,
          voice,
          speed,
          pitch
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const endTime = performance.now();
      const totalLatency = endTime - startTime;

      setResult({
        ...response.data,
        latencyMs: totalLatency // Total latency (API call + download)
      });

      console.log('🎤 Azure Neural TTS generated successfully:', response.data);
    } catch (err: any) {
      console.error('🎤 Azure Neural TTS error:', err);
      if (err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
        setError('Cannot connect to proxy server. Make sure proxy is running on http://localhost:3003 (run: node tts-proxy-server.js)');
      } else if (err.response?.status === 404) {
        setError('Proxy server endpoint not found. Make sure tts-proxy-server.js is running.');
      } else if (err.response?.data?.error) {
        setError(`Error: ${err.response.data.error}`);
      } else if (err.response?.data?.error?.message) {
        setError(`Azure API Error: ${err.response.data.error.message}`);
      } else {
        setError(`Error: ${err.message || 'Failed to generate speech'}`);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePlay = () => {
    if (result && audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const costPer1MChars = 16.00;
  const freeTierChars = 500_000;
  const estimatedCost = Math.max(0, (text.length - freeTierChars) / 1_000_000) * costPer1MChars;
  const isFree = text.length <= freeTierChars;

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2 text-gray-900 dark:text-white">
          Azure Neural TTS Prototype
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Test Microsoft Azure's Neural TTS ($16/1M chars, 500K free/month)
        </p>
      </div>

      {/* Voice Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Voice (200+ neural voices available)
        </label>
        <select
          value={voice}
          onChange={(e) => setVoice(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          disabled={isGenerating}
        >
          {voices.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Azure supports 50+ languages with regional accents (US, UK, AU, IN, etc.)
        </p>
      </div>

      {/* Text Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Text to speak ({text.length} characters)
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="w-full h-32 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
          placeholder="Enter text to convert to speech..."
          disabled={isGenerating}
        />
      </div>

      {/* Speed Control */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Speed: {speed.toFixed(2)}x
        </label>
        <input
          type="range"
          min="0.5"
          max="2.0"
          step="0.1"
          value={speed}
          onChange={(e) => setSpeed(parseFloat(e.target.value))}
          className="w-full"
          disabled={isGenerating}
        />
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>0.5x (Slow)</span>
          <span>1.0x (Normal)</span>
          <span>2.0x (Fast)</span>
        </div>
      </div>

      {/* Pitch Control */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Pitch: {pitch}
        </label>
        <select
          value={pitch}
          onChange={(e) => setPitch(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          disabled={isGenerating}
        >
          {pitchOptions.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      {/* Cost Estimate */}
      <div className={`mb-6 p-4 rounded-lg ${isFree ? 'bg-green-50 dark:bg-green-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20'}`}>
        <p className={`text-sm font-medium ${isFree ? 'text-green-900 dark:text-green-300' : 'text-yellow-900 dark:text-yellow-300'}`}>
          💰 Estimated cost: <span className="font-bold">
            {isFree ? 'FREE' : `$${estimatedCost.toFixed(6)}`}
          </span>
        </p>
        <p className={`text-xs mt-1 ${isFree ? 'text-green-700 dark:text-green-400' : 'text-yellow-700 dark:text-yellow-400'}`}>
          {isFree
            ? `Free tier: ${text.length.toLocaleString()} / 500,000 characters used this month`
            : `Based on $16.00 per 1M characters (after 500K free). Current text: ${text.length} chars.`
          }
        </p>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={isGenerating || !text.trim()}
        className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors mb-6"
      >
        {isGenerating ? '⏳ Generating...' : '🚀 Generate Speech (Azure Neural TTS)'}
      </button>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <p className="text-sm font-medium text-red-900 dark:text-red-300">
            ❌ {error}
          </p>
        </div>
      )}

      {/* Results */}
      {result && (
        <>
          {/* Latency & Stats */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h3 className="font-bold text-sm mb-3 text-blue-900 dark:text-blue-300">
              📊 Generation Stats
            </h3>
            <div className="space-y-2 text-sm text-blue-800 dark:text-blue-400">
              <div className="flex justify-between">
                <span>⚡ Total latency (API + download):</span>
                <span className="font-bold">{result.latencyMs.toFixed(0)}ms</span>
              </div>
              <div className="flex justify-between">
                <span>📦 Audio file size:</span>
                <span className="font-bold">{(result.audioSizeBytes / 1024).toFixed(1)} KB</span>
              </div>
              <div className="flex justify-between">
                <span>💵 Actual cost:</span>
                <span className="font-bold">
                  {isFree ? 'FREE (within 500K tier)' : `$${result.costEstimate.toFixed(6)}`}
                </span>
              </div>
              <div className="flex justify-between">
                <span>✅ Quality assessment:</span>
                <span className="font-bold">
                  {result.latencyMs < 1000 ? '🟢 Excellent' :
                   result.latencyMs < 3000 ? '🟡 Good' :
                   '🔴 Slow'}
                </span>
              </div>
            </div>
          </div>

          {/* Audio Player */}
          <div className="mb-6">
            <audio
              ref={audioRef}
              src={result.audioUrl}
              onEnded={() => setIsPlaying(false)}
              className="w-full mb-4"
              controls
            />

            <div className="flex gap-3">
              <button
                onClick={handlePlay}
                disabled={isPlaying}
                className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
              >
                ▶️ Play
              </button>
              <button
                onClick={handlePause}
                disabled={!isPlaying}
                className="flex-1 px-6 py-3 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
              >
                ⏸️ Pause
              </button>
              <button
                onClick={handleStop}
                className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
              >
                ⏹️ Stop
              </button>
            </div>
          </div>
        </>
      )}

      {/* Testing Checklist */}
      <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-6 bg-gray-50 dark:bg-gray-900">
        <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">
          📋 Testing Checklist
        </h3>
        <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Test multiple voices - Compare US, UK, Australian accents</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Compare voice quality vs Web Speech API and OpenAI</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Test SSML pitch control (x-low to x-high)</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Test on iPhone 16 Pro - Measure latency</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Test on Google Pixel 9 - Measure latency</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Test on iPad - Verify playback quality</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Test speed variations (0.5x, 1.0x, 1.5x, 2.0x)</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Measure bandwidth usage (audio file size)</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Rate voice naturalness (1-10 scale)</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Verify 500K free tier is sufficient for testing</label>
          </div>
        </div>
      </div>

      {/* Azure Features */}
      <div className="mt-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
        <h4 className="font-bold text-sm mb-2 text-purple-900 dark:text-purple-300">
          ✨ Azure Neural TTS Features
        </h4>
        <ul className="text-xs text-purple-700 dark:text-purple-400 space-y-1 list-disc list-inside">
          <li>200+ neural voices across 50+ languages</li>
          <li>SSML support (pitch, rate, volume, pauses, emphasis)</li>
          <li>Voice styles (cheerful, sad, angry, empathetic, newscast)</li>
          <li>500K free characters per month (great for testing)</li>
          <li>Regional accents (US, UK, AU, IN, CA, etc.)</li>
          <li>High-quality neural voices (comparable to OpenAI)</li>
        </ul>
      </div>

      {/* Implementation Note */}
      <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
        <h4 className="font-bold text-sm mb-2 text-orange-900 dark:text-orange-300">
          ⚠️ Backend Implementation Required
        </h4>
        <p className="text-xs text-orange-700 dark:text-orange-400 mb-2">
          This prototype requires a backend endpoint to securely call the Azure API (API key must not be exposed to frontend).
        </p>
        <div className="text-xs text-orange-800 dark:text-orange-500 font-mono bg-white dark:bg-gray-900 p-2 rounded">
          POST /api/app/tts-prototype/azure<br/>
          Request: &#123; text: string, voice: string, speed: number, pitch: string &#125;<br/>
          Response: &#123; audioUrl: string, audioSizeBytes: number, costEstimate: number &#125;
        </div>
      </div>
    </div>
  );
};

export default AzureTTSPrototype;
